import {
  Captions, ChevronDown, Ellipsis, Headphones, MicOff, Pause, Phone, PhoneIncoming,
  PhoneOff, Repeat, Users,
  X, Zap,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Tooltip } from "@/components/ui/misc";
import {
  Popover, PopoverAnchor, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { holdsInRoom } from "@/lib/acd";
import { useMediaQuery } from "@/lib/use-media-query";
import { cn, formatDuration, formatPhoneNumber } from "@/lib/utils";
import {
  AcdCallStrip, ConsultBar, DispositionView, MonitorBar, useAcdCall, useCampaignAnswers,
  useCampaignAutoAnswer, useJoiningListenIn, useMonitorAutoAnswer,
} from "@/softphone/acd-call";
import { callAnnouncements, type AnnouncedCall, type Announcement } from "@/softphone/announce";
import {
  AVAILABILITY_TEXT, AvailabilityMenu, useRepAvailability,
} from "@/softphone/availability";
import { isEngaged } from "@/softphone/call-control";
import {
  CallControls, describeParty, EndCallButton, IncomingBanner, KeypadView, ListenInConnecting,
  ParkedRow, RingingMark,
  partyName, stateText, TransferDialog, typingElsewhere, type SubView,
} from "@/softphone/call-views";
import { useStageMounted } from "@/softphone/stage";
import type { ConferenceState } from "@/softphone/conference";
import {
  CallRoster, DirectConsultBar, InviteStrip, useCallerHeld, useRoomPeople,
} from "@/softphone/direct-call";
import { AddParticipantDialog, ParticipantBar } from "@/softphone/participant";
import { openAudioSetup, useMicPermission } from "@/softphone/audio-setup";
import { DialerView, useDialerWork } from "@/softphone/dialer-offer";
import {
  ManualCallView, useManualCallWork, useOwnCallAutoAnswer, useOwnManualRinging,
} from "@/softphone/manual-call";
import { Dialler } from "@/softphone/dialler";
import { BlockCallerAction, BlockCallerHost } from "@/softphone/block-caller";
import { LeadCardPanel } from "@/softphone/lead-card";
import { TranscriptPanel, useCallTranscription } from "@/softphone/transcription-widget";
import {
  useCallDuration, useSoftphone, type Call, type RegistrationState,
} from "@/softphone/softphone-context";
import type { MicPermission } from "@/softphone/audio-devices";

/** From here up the phone is a rail of the layout rather than a card floating over it:
 *  Tailwind's `md`, the same width the sidebar appears at. */
const DOCKED = "(min-width: 48rem)";

/** How long the end of a call may take to reach the rep's record before a phone that opened itself
 *  for the call stops waiting for the disposition window that may follow it, and closes. */
const CALL_END_SETTLES_MS = 4_000;

/**
 * The phone, beside every screen.
 *
 * Beside rather than on its own page because a rep is never *only* on the phone — they are
 * reading a lead while it rings. On a desktop-width window it is a slim rail at the right edge
 * of the layout (`PhoneRail`), and the phone itself — the dialler, a call ringing, the call in
 * progress — opens from that rail as a popover over the page's top-right corner. The page keeps
 * its width, and nothing on it is covered until the phone is opened.
 *
 * It has been both of the other shapes. Floating in the bottom-right corner, it sat on top of
 * whatever a page put there — a table's last rows, the pager's Next button. As a 320px column
 * beside the page it covered nothing, but cost the page too much of its width.
 *
 * A narrow window has no width for a rail, so there the phone floats as it always did — a pill
 * that opens into a card — and the shell leaves room under every page to scroll clear of it.
 *
 * It opens itself when a call arrives: an inbound call the rep has to miss because the phone was
 * closed is the failure mode this whole surface exists to prevent, so a ringing call's popover
 * also ignores clicks elsewhere on the page. When the phone opened itself, it closes itself again
 * once the last call ends — and the rail keeps saying what the phone is doing either way. The one
 * screen it never opens itself over is the Phone page, which is this same phone at full size.
 *
 * ## One view at a time
 *
 * The dock is a small screen, not a stack of panels. Exactly one thing is in front of the rep:
 *
 *     connect / standby   the phone is not registered here; one button says what to do
 *     dialler             registered, no calls
 *     incoming            the first call ringing — Answer and Decline, nothing else
 *     call                the leg the rep is on: who, how long, five labelled controls, End call
 *       ├ keypad          DTMF into that call
 *       └ transfer        every hand-over the phone can make right now, in people terms
 *
 * The two under `call` are pushed over it and popped with Back (or Escape), and focus returns
 * to the tile that opened them. A second call ringing, and calls parked on hold, are rows
 * *inside* the call view rather than panes competing with it: the rep is on one call, and
 * everything else is arranged around that fact.
 *
 * ## Buttons a rep does not have to decode
 *
 * Every control carries its own word. The old strip was icon-only with tooltips, which are
 * invisible to a keyboard, to a touch screen, and to anybody who has not learned the glyph. The
 * five call controls are tiles — icon over a label — in fixed positions, so Mute is always
 * top-left and Transcribe always bottom-right; a control that does not apply yet is dimmed with
 * a reason rather than removed, which keeps the grid from rearranging under a hand that is
 * reaching for it. End call is the one red, full-width control and it is always at the bottom.
 *
 * Transfer is one action, not three. Moving a caller into the rep's room, introducing two
 * callers to each other and sending a caller to a number are three switch commands but one
 * intention, so they are one tile that opens one list — see `transferOptions`.
 *
 * ## What a screen reader hears
 *
 * Calls arriving, connecting, going on hold and ending are announced through a live region
 * (`callAnnouncements`), an incoming call assertively. The Answer button takes focus when a call
 * arrives unless the rep is typing somewhere, in which case the announcement has to do — pulling
 * focus out of a note mid-sentence would be the phone interrupting the job it exists to support.
 */
export function SoftphoneDock() {
  const phone = useSoftphone();
  // Here because the dock is on every screen: a campaign's auto-answer must not depend on the page
  // the rep has open when the ACD rings them. Nor may a supervisor's call into a rep's call, which
  // they ask for on the floor and take on this phone.
  useCampaignAutoAnswer();
  useOwnCallAutoAnswer();
  useMonitorAutoAnswer();
  const docked = useMediaQuery(DOCKED);
  const [open, setOpenState] = React.useState(false);
  const [view, setView] = React.useState<SubView | null>(null);
  const panelRef = React.useRef<HTMLElement>(null);
  const foldedRef = React.useRef<HTMLButtonElement>(null);
  // `open`, for the effects below that read it but must not re-run when it changes.
  const openRef = React.useRef(false);
  // Set when the REP opened or closed the phone. The phone opening itself for a call must not
  // move focus — the rep may be typing — but opening it from the rail or the pill, or closing it
  // from its own header, should land the keyboard somewhere sensible instead of on <body>.
  const focusAfterToggle = React.useRef(false);
  // Set when the PHONE opened itself for a call, and cleared the moment the rep opens or closes
  // it: from then on, open or closed is the rep's decision and nothing here overrides it.
  const openedForCall = React.useRef(false);
  // Set when the phone closes itself while the keyboard is somewhere else on the page. The
  // popover hands focus back to the rail as it closes, which would pull the caret out of a note
  // the rep is in the middle of typing.
  const keepFocusOnClose = React.useRef(false);
  // The sub-view Back has just closed, so focus can go to the tile that opened it once the
  // call view is on screen again — an effect below, not a timer, because the tile does not
  // exist until React has committed the swap.
  const returnFocusTo = React.useRef<SubView | null>(null);

  const ringing = phone.calls.some((c) => c.direction === "inbound" && c.state === "ringing");
  const hasCalls = phone.calls.length > 0;
  const acdState = useRepAvailability()?.view.state ?? null;
  // The ACD's post-call window, whose outcome is chosen in here (`DispositionView`).
  const inWindow = acdState === "disposition";
  // The rep's record still has them on the ACD's call. Once the phone's last call has gone, that is
  // the record running behind: the event-tracker writes the end of a call, and the window that
  // follows it, a moment after the phone sees the call end.
  const recordOnCall = acdState === "ringing" || acdState === "busy";
  // A lead the dialer offers, or is calling for this rep (`DialerView`).
  const work = useDialerWork();
  const offerId = work.offer?.campaign_lead_id ?? null;
  // A number the rep dialed by hand, on its way or just failed (`ManualCallView`, ADR-047).
  const manual = useManualCallWork();
  const manualId = manual.shown ? (manual.call?.call_id ?? null) : null;
  const announcement = useCallAnnouncements(phone.calls, phone.conference);

  const setOpen = React.useCallback((next: boolean) => {
    openRef.current = next;
    setOpenState(next);
  }, []);

  // Keyed on the arrival of a call, never on `open`: a rep who closes the phone mid-call has
  // decided, and an effect that re-ran on the close would open it straight back up.
  // By the leg's id rather than the leg, too. Every change to a live call — Mute, Hold, a
  // transcript switched on — rebuilds the call object, and an effect keyed on the object read
  // each of those as a new call and pushed the phone back open under the rep's hand.
  // An offer from the dialer opens it too: an offer lasts about a minute, and it is keyed on the
  // offer for the same reason. So does a call the rep placed by hand from a number in a table:
  // the card that follows it is in here, and the rep may have clicked from any screen.
  // Nothing opens it while the Phone page is on screen. That page shows the call at full size and
  // carries the offer and the outcome window itself, so a popover over it would be the same phone
  // twice; there, opening it is the rep's own click.
  const stage = useStageMounted();
  const primaryId = phone.primary?.id ?? null;
  React.useEffect(() => {
    if (stage) return;
    if (!ringing && primaryId === null && offerId === null && manualId === null) return;
    if (!openRef.current) openedForCall.current = true;
    setOpen(true);
  }, [ringing, primaryId, offerId, manualId, setOpen, stage]);

  // ...and closes again once the calls it opened for are over — after an ACD call, once the line's
  // disposition window has closed as well: the outcome is chosen in here, and a phone that folded
  // away the moment the caller hung up would hide the one thing left to do. The window reaches the
  // phone a moment after the call has gone, and nothing opens the phone for it, so while the rep's
  // record still has them on the call the phone waits for the record to catch up — for a few
  // seconds at most, since a record that never does must not hold the phone open.
  React.useEffect(() => {
    if (
      hasCalls ||
      inWindow ||
      offerId !== null ||
      work.dialing ||
      manualId !== null ||
      !openedForCall.current
    ) {
      return;
    }
    const close = () => {
      // The rep opened or closed the phone while it waited, which makes it theirs.
      if (!openedForCall.current) return;
      openedForCall.current = false;
      // Whatever had focus in the call view went away with the call. Land the keyboard on the
      // rail or the pill rather than on <body> — unless it is somewhere else on the page, such as
      // a note the rep is typing, which keeps it.
      const focused = document.activeElement;
      if (!focused || focused === document.body || panelRef.current?.contains(focused)) {
        focusAfterToggle.current = true;
      } else {
        keepFocusOnClose.current = true;
      }
      setOpen(false);
    };
    if (!recordOnCall) {
      close();
      return;
    }
    const timer = window.setTimeout(close, CALL_END_SETTLES_MS);
    return () => window.clearTimeout(timer);
  }, [hasCalls, inWindow, recordOnCall, offerId, work.dialing, manualId, setOpen]);

  // A sub-view belongs to the leg it was opened over. When the phone moves the rep to another
  // leg — the call ended, a swap, a hand-over that took the leg away — the view goes with it, or
  // the rep is left looking at a keypad for a call that no longer exists.
  const activeId = phone.activeCallId;
  React.useEffect(() => {
    setView(null);
  }, [activeId]);

  React.useEffect(() => {
    if (!focusAfterToggle.current) return;
    const target = open ? panelRef.current : foldedRef.current;
    // Docked, the phone's content is portalled in on the commit after this one, so there is no
    // panel to focus yet; the popover's `onOpenAutoFocus` finishes the job once there is.
    if (!target) return;
    focusAfterToggle.current = false;
    target.focus();
  }, [open]);

  const toggle = (next: boolean) => {
    focusAfterToggle.current = true;
    openedForCall.current = false;
    setOpen(next);
  };
  // The popover closing itself — a click somewhere else on the page, or Escape. Radix already
  // puts focus where each of those wants it (back on the rail after Escape, left where the rep
  // clicked after a click away), so unlike `toggle` this does not move it.
  const dismiss = () => {
    openedForCall.current = false;
    setOpen(false);
  };
  React.useEffect(() => {
    if (view !== null || !returnFocusTo.current) return;
    const tile = returnFocusTo.current;
    returnFocusTo.current = null;
    panelRef.current?.querySelector<HTMLElement>(`[data-tile="${tile}"]`)?.focus();
  }, [view]);

  const popView = () => {
    returnFocusTo.current = view;
    setView(null);
  };

  if (!phone.available) return null;

  const panel = (
    <section
      ref={panelRef}
      tabIndex={-1}
      aria-label="Phone"
      // Docked, Escape is the popover's to handle, below: it sees the key before this would.
      onKeyDown={
        docked
          ? undefined
          : (e) => {
              if (e.key === "Escape" && view) {
                e.stopPropagation();
                popView();
              }
            }
      }
      className={
        docked
          ? "flex min-h-0 flex-1 flex-col outline-none"
          : cn(
              "pointer-events-auto flex w-full flex-col overflow-hidden rounded-xl border bg-surface shadow-lg",
              ringing ? "border-success-9" : "border-border",
            )
      }
    >
      <Header docked={docked} onMinimise={() => toggle(false)} />
      <div
        className={cn(
          "min-h-0 overflow-y-auto p-3 scrollbar-subtle",
          !docked && "max-h-[calc(100dvh-7rem)]",
        )}
      >
        {phone.error && (
          <Callout tone="danger" className="mb-3">
            {phone.error}
          </Callout>
        )}
        <MicBlocked />
        <Body view={view} onPush={setView} onPop={popView} />
      </div>
      {docked && <TranscriptPanel />}
    </section>
  );

  if (!docked) {
    return (
      <div className="pointer-events-none fixed bottom-4 right-4 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col items-end">
        <Announcer announcement={announcement} />
        {open ? panel : <Pill buttonRef={foldedRef} onOpen={() => toggle(true)} />}
        <BlockCallerHost />
      </div>
    );
  }

  return (
    <>
    <Popover open={open} onOpenChange={(next) => (next ? toggle(true) : dismiss())}>
      {/* The rail is the anchor, so the popover opens flush against its left edge, at the top. */}
      <PopoverAnchor asChild>
        <div
          className={cn(
            "flex h-full w-14 shrink-0 flex-col items-center border-l bg-surface",
            ringing ? "border-success-9" : "border-border",
          )}
        >
          <Announcer announcement={announcement} />
          <PhoneRail open={open} triggerRef={foldedRef} />
        </div>
      </PopoverAnchor>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={8}
        className={cn(
          "flex w-80 flex-col overflow-hidden rounded-xl bg-surface p-0",
          "max-h-[var(--radix-popover-content-available-height)]",
          ringing && "border-success-9",
        )}
        // Focus is the dock's to place: into the phone when the rep opened it — the flag `toggle`
        // set, which the effect above could not act on before this content existed — and nowhere
        // at all when a call opened it while they were typing.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (!focusAfterToggle.current) return;
          focusAfterToggle.current = false;
          panelRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          if (!keepFocusOnClose.current) return;
          keepFocusOnClose.current = false;
          e.preventDefault();
        }}
        // A ringing call is not dismissed by a click somewhere else on the page. Nor is the
        // phone dismissed by its own Transfer dialog: that is portalled outside the popover, so
        // the focus it takes when it opens reaches this as an interaction somewhere else.
        onInteractOutside={(e) => {
          if (ringing || view === "transfer" || view === "add") e.preventDefault();
        }}
        // Escape backs out of the keypad first, and closes the phone only from the view
        // underneath it. Escape over the Transfer dialog never reaches here: the dialog is the
        // top layer and closes itself, which pops the view.
        onEscapeKeyDown={(e) => {
          if (!view) return;
          e.preventDefault();
          popView();
        }}
      >
        {panel}
      </PopoverContent>
    </Popover>
    <BlockCallerHost />
    </>
  );
}


function Body({
  view, onPush, onPop,
}: {
  view: SubView | null;
  onPush: (view: SubView) => void;
  onPop: () => void;
}) {
  const phone = useSoftphone();
  const inWindow = useRepAvailability()?.view.state === "disposition";
  const work = useDialerWork();
  const manual = useManualCallWork();
  const ownRinging = useOwnManualRinging();
  const joining = useJoiningListenIn(
    (phone.calls.find((c) => c.id === phone.activeCallId) ?? phone.primary)?.id ?? null,
  );
  if (phone.registration === "standby") return <StandbyView />;
  if (phone.registration !== "registered") return <ConnectView />;

  const active = phone.calls.find((c) => c.id === phone.activeCallId) ?? phone.primary;
  if (!active) {
    // After an ACD call the line waits for the outcome, and the rep's next call waits with it.
    if (inWindow) return <DispositionView />;
    // A number the rep dialed by hand: the card follows it until it connects or fails.
    if (manual.shown) return <ManualCallView />;
    return work.offer || work.dialing ? <DialerView /> : <Dialler />;
  }
  if (active.direction === "inbound" && active.state === "ringing") {
    // The ACD ringing this phone with the call the rep placed: the phone answers it itself, and
    // the card keeps saying "they answered" rather than flashing an Answer button for a moment.
    if (manual.shown && ownRinging === active.id) return <ManualCallView />;
    // A supervisor's listen-in, which the phone answers itself: connecting, not Answer / Decline.
    if (joining) return <ListenInConnecting call={active} />;
    return <IncomingView call={active} />;
  }
  return (
    <>
      {view === "keypad" ? (
        <KeypadView call={active} onBack={onPop} />
      ) : (
        <CallView call={active} onPush={onPush} />
      )}
      {/* Centred over the console rather than squeezed into 320px, with the call still in the
          dock behind it. The popover below refuses to dismiss while either is open. */}
      <TransferDialog open={view === "transfer"} onClose={onPop} />
      <AddParticipantDialog open={view === "add"} callId={active.id} onClose={onPop} />
    </>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* Words                                                                                       */
/* ------------------------------------------------------------------------------------------ */

const REGISTRATION_LABELS: Record<RegistrationState, string> = {
  idle: "Not connected",
  connecting: "Connecting…",
  // Not "Ready": that is the ACD's word for taking calls, shown beside this one in the header.
  registered: "Connected",
  standby: "On another tab",
  failed: "Disconnected",
};

/* ------------------------------------------------------------------------------------------ */
/* Announcements                                                                               */
/* ------------------------------------------------------------------------------------------ */

function useCallAnnouncements(
  calls: Call[],
  conference: ConferenceState | null,
): Announcement | null {
  const [current, setCurrent] = React.useState<Announcement | null>(null);
  const previous = React.useRef<AnnouncedCall[]>([]);

  React.useEffect(() => {
    const next = calls.map((c) => ({
      id: c.id,
      direction: c.direction,
      state: c.state,
      muted: c.muted,
      label: partyName(c, conference),
    }));
    const news = callAnnouncements(previous.current, next);
    previous.current = next;
    if (news.length === 0) return;
    setCurrent({
      text: `${news.map((n) => n.text).join(". ")}.`,
      urgent: news.some((n) => n.urgent),
    });
  }, [calls, conference]);

  // Cleared once it has been read, so the same sentence can be announced again next time: a
  // live region speaks only when its content CHANGES, and two "Microphone muted" in a row
  // would otherwise be one.
  React.useEffect(() => {
    if (!current) return;
    const t = setTimeout(() => setCurrent(null), 4000);
    return () => clearTimeout(t);
  }, [current]);

  return current;
}

/** Two regions rather than one that changes politeness: a live region is registered with its
 *  politeness at mount, and flipping the attribute afterwards is not reliably honoured. */
function Announcer({ announcement }: { announcement: Announcement | null }) {
  return (
    <>
      <div role="status" aria-live="polite" className="sr-only">
        {announcement && !announcement.urgent ? announcement.text : ""}
      </div>
      <div role="alert" aria-live="assertive" className="sr-only">
        {announcement?.urgent ? announcement.text : ""}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* Frame: header, pill                                                                         */
/* ------------------------------------------------------------------------------------------ */

function Header({ docked, onMinimise }: { docked: boolean; onMinimise: () => void }) {
  const phone = useSoftphone();
  const extension = phone.credentials?.extension;
  // The taking-calls control shares this row, and a 320px phone does not fit it beside
  // "Connected · ext. 1001": the extension is what truncated away. Beside it, the number stands
  // on its own.
  const compact = useRepAvailability() !== null;
  return (
    <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-surface-sunken pl-3 pr-1.5">
      <span
        className={cn("size-2 shrink-0 rounded-full", registrationDot(phone.registration))}
        aria-hidden
      />
      <p className="min-w-0 flex-1 truncate text-xs font-medium text-text">
        {REGISTRATION_LABELS[phone.registration]}
        {extension && (
          <span className="font-normal text-text-muted">
            {compact ? " · " : " · ext. "}
            {extension}
          </span>
        )}
      </p>
      <AvailabilityMenu />
      <PhoneMenu />
      <Button
        variant="ghost"
        size="icon"
        onClick={onMinimise}
        aria-label={docked ? "Hide the phone" : "Minimise the phone"}
      >
        {/* Docked it closes a popover and the rail stays; floating it folds down into the pill. */}
        {docked ? <X /> : <ChevronDown />}
      </Button>
    </div>
  );
}

/** A registration as the colour of its dot: green when calls can reach the rep, amber while that
 *  is about to be true, grey otherwise. The header and the rail both show one. */
function registrationDot(registration: RegistrationState): string {
  if (registration === "registered") return "bg-success-9";
  if (registration === "connecting") return "bg-warning-9";
  return "bg-gray-7";
}

/**
 * The phone's two settings, behind one button.
 *
 * Auto-answer changes what the machine does the moment a call arrives, so its *state* has to be
 * visible at all times — a rep who forgets it is on has the room hearing their side of a call
 * they did not choose to take. The control itself does not need to be: it is set once a shift.
 * So the trigger shows a lit bolt while auto-answer is on and the switch lives in the menu.
 *
 * Disconnecting tears the client down, which hangs every live call up with it. One stray click
 * would drop a lead mid-sentence, so it is refused while anything is in progress — hang up
 * first, which is a deliberate act.
 */
/**
 * The one microphone state that belongs on the phone itself rather than in its menu.
 *
 * A blocked microphone does not look like a fault: the phone registers, the rep goes Ready, calls
 * ring and are answered — and every lead hears silence, which the rep finds out about from the
 * lead or not at all. So it is said where they are already looking, with the way to fix it one
 * press away. The other states are quiet: "not asked yet" opens the dialog by itself at sign-in,
 * and a granted microphone has nothing to report.
 */
function MicBlocked() {
  const permission = useMicPermission();
  if (permission !== "denied") return null;
  return (
    <Callout tone="warning" className="mb-3">
      <p>This browser is blocking your microphone, so callers will not hear you.</p>
      <button
        type="button"
        className="mt-1 font-medium underline underline-offset-2"
        onClick={() => openAudioSetup()}
      >
        How to fix it
      </button>
    </Callout>
  );
}

/**
 * What the menu says under "Microphone and speaker".
 *
 * A blocked microphone is the one state worth spelling out here: the phone registers, calls
 * reach it, it rings — and nobody on the other end can hear the rep. Everything else is the
 * ordinary case and says what the item is for rather than raising an alarm about it.
 */
function micStatusText(permission: MicPermission | null): string {
  if (permission === "denied") return "Blocked by this browser. Calls will have no sound from you.";
  if (permission === "unavailable") return "This browser cannot use a microphone here.";
  if (permission === "prompt") return "Not set up yet. Do it before your first call.";
  return "Choose which devices your calls use.";
}

/** What the options button is worth saying out loud — a state a rep might not have meant. The
 *  campaign's auto-answer counts: it changes what the phone does as much as the browser's does. */
function menuHint(autoAnswer: boolean, campaignAnswers = false): string {
  if (autoAnswer) return "Auto-answer is on";
  return campaignAnswers ? "Your campaign answers its calls" : "Phone options";
}

export function PhoneMenu() {
  const phone = useSoftphone();
  const campaignAnswers = useCampaignAnswers();
  const micPermission = useMicPermission();
  const onCall = phone.calls.length > 0;
  const hint = menuHint(phone.autoAnswer, campaignAnswers);
  return (
    <DropdownMenu>
      <Tooltip content={hint}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Phone options. ${hint}`}>
            {/* Auto-answer changes what the phone DOES, so it shows on the button; off is the
                ordinary state and gets no icon. */}
            {phone.autoAnswer || campaignAnswers ? <Zap className="text-accent-11" /> : <Ellipsis />}
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuCheckboxItem
          checked={phone.autoAnswer}
          onCheckedChange={(checked) => phone.setAutoAnswer(checked === true)}
        >
          <span className="flex min-w-0 flex-col">
            <span>Auto-answer calls</span>
            <span className="text-xs text-text-muted">
              {campaignAnswers
                ? "Your campaign already answers its own calls. This is for your other calls, in this browser."
                : "This browser only. Never over a call in progress."}
            </span>
          </span>
        </DropdownMenuCheckboxItem>
        {/* No "Ring for calls" switch: the phone always rings (`tones.ts`). The speaker it rings
            through is the setup dialog's business, one item down. */}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openAudioSetup()}>
          <Headphones />
          <span className="flex min-w-0 flex-col">
            <span>Microphone and speaker</span>
            <span className="text-xs text-text-muted">{micStatusText(micPermission)}</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          disabled={onCall || phone.registration !== "registered"}
          onSelect={() => phone.disconnect()}
        >
          <PhoneOff />
          Disconnect the phone
        </DropdownMenuItem>
        <p className="px-2 pb-1 pt-0.5 text-xs text-text-muted">
          {onCall
            ? "End your calls before disconnecting."
            : "Calls stop reaching you until you connect again."}
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * What the phone has to say while it is closed, worked out once for both of its shapes so the
 * pill and the rail cannot disagree. It says what the phone is doing, not just that it exists: a
 * rep who closed it mid-call still sees the timer, and a muted microphone shows as one.
 */
interface FoldedStatus {
  incoming: boolean;
  onCall: boolean;
  muted: boolean;
  /** The call in front of the rep is on hold. */
  held: boolean;
  /** Who, or that a call is ringing, or just "Phone". */
  text: string;
  /** How long, or what the call or the registration is doing. */
  detail: string | null;
  /** One word, for a rail 56px wide. */
  short: string;
}

/** A call that has not connected, in one word. Engaged calls show their timer instead. */
const SHORT_STATE: Record<Call["state"], string> = {
  trying: "Calling",
  ringing: "Ringing",
  active: "Connected",
  held: "On hold",
  ended: "Ended",
};

function useFoldedStatus(): FoldedStatus {
  const phone = useSoftphone();
  const incoming = phone.calls.find((c) => c.direction === "inbound" && c.state === "ringing");
  const active = phone.calls.find((c) => c.id === phone.activeCallId) ?? null;
  const seconds = useCallDuration(active?.answeredAt ?? null);
  const joining = useJoiningListenIn(incoming?.id ?? null);

  if (incoming && joining) {
    return {
      incoming: false,
      onCall: true,
      muted: false,
      held: false,
      text: "Connecting to call session",
      detail: "Please wait",
      short: "Joining",
    };
  }
  if (incoming) {
    return {
      incoming: true,
      onCall: active !== null,
      muted: false,
      held: false,
      text: "Incoming call",
      detail: partyName(incoming, phone.conference),
      short: "Incoming",
    };
  }
  if (active) {
    const engaged = isEngaged(active.state);
    return {
      incoming: false,
      onCall: true,
      muted: active.muted,
      held: active.state === "held",
      text: partyName(active, phone.conference),
      detail: engaged
        ? `${formatDuration(seconds)}${active.muted ? " · muted" : ""}`
        : stateText(active, phone.handingOver.includes(active.id)),
      short: engaged ? formatDuration(seconds) : SHORT_STATE[active.state],
    };
  }
  return {
    incoming: false,
    onCall: false,
    muted: false,
    held: false,
    text: "Phone",
    detail: REGISTRATION_LABELS[phone.registration],
    short: "Phone",
  };
}

function foldedLabel(status: FoldedStatus): string {
  return `Open the phone. ${status.text}${status.detail ? `, ${status.detail}` : ""}`;
}

function FoldedGlyph({ status }: { status: FoldedStatus }) {
  return (
    <span
      className={cn(
        "relative flex size-8 shrink-0 items-center justify-center rounded-full",
        status.incoming
          ? "bg-success-9 text-white"
          : status.onCall
            ? "bg-accent-3 text-accent-11"
            : "bg-surface-sunken text-text-muted",
      )}
    >
      {status.incoming ? (
        <PhoneIncoming className="size-4" aria-hidden />
      ) : status.muted ? (
        <MicOff className="size-4" aria-hidden />
      ) : (
        <Phone className="size-4" aria-hidden />
      )}
      {status.incoming && <span className="animate-pulse-ring" aria-hidden />}
    </span>
  );
}

/** The folded phone on a narrow window, floating bottom-right. */
function Pill({
  buttonRef, onOpen,
}: {
  buttonRef: React.Ref<HTMLButtonElement>;
  onOpen: () => void;
}) {
  const phone = useSoftphone();
  const status = useFoldedStatus();
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onOpen}
      aria-expanded={false}
      aria-label={foldedLabel(status)}
      className={cn(
        "pointer-events-auto flex h-11 max-w-full items-center gap-2.5 rounded-full border bg-surface",
        "pl-1.5 pr-4 shadow-lg transition-colors hover:bg-surface-hover",
        status.incoming ? "border-success-9" : "border-border",
      )}
    >
      <FoldedGlyph status={status} />
      <span className="flex min-w-0 flex-col items-start leading-tight">
        <span className="max-w-48 truncate text-sm font-medium text-text">{status.text}</span>
        {status.detail && (
          <span className="max-w-48 truncate text-xs tabular text-text-muted">
            {status.detail}
          </span>
        )}
      </span>
      {!status.incoming && !status.onCall && (
        <span
          className={cn(
            "size-2 shrink-0 rounded-full",
            phone.registration === "registered" ? "bg-success-9" : "bg-gray-7",
          )}
          aria-hidden
        />
      )}
    </button>
  );
}

/**
 * The phone's rail: 56px at the right edge of the layout, always there on a desktop-width window
 * and never over the page. Its one control opens the phone as a popover. Under it the rail says
 * what the phone is doing, so a rep who closed the popover still sees it at a glance — a call
 * ringing, the call's timer, on hold, muted, transcribing — along with the one setting that
 * changes what the phone does on its own, auto-answer, the same one the options button shows
 * inside the phone.
 *
 * Who the call is with does not fit in 56px, so it is in the button's name, and in a tooltip for
 * a pointer. Only for a pointer: closing the popover hands focus back to this button on purpose,
 * and a tooltip that opened on focus would pop up every time. It is also held shut while the
 * popover is open, where it would sit on top of the phone's own header.
 */
function PhoneRail({
  open, triggerRef,
}: {
  open: boolean;
  triggerRef: React.Ref<HTMLButtonElement>;
}) {
  const phone = useSoftphone();
  const transcription = useCallTranscription();
  const status = useFoldedStatus();
  const acd = useRepAvailability();
  const work = useDialerWork();
  const manual = useManualCallWork();
  const campaignAnswers = useCampaignAnswers();
  const [tipOpen, setTipOpen] = React.useState(false);
  const idle = !status.incoming && !status.onCall;
  const facts = [
    idle ? null : status.text,
    status.detail,
    acd ? `taking calls: ${acd.view.label}` : null,
    work.offer ? "a lead is offered to you" : null,
    manual.shown && manual.call
      ? manual.call.stage === "ended"
        ? `your call to ${formatPhoneNumber(manual.call.phone)} did not connect`
        : `calling ${formatPhoneNumber(manual.call.phone)}`
      : null,
    transcription.on ? "transcribing" : null,
    phone.autoAnswer
      ? "auto-answer is on"
      : campaignAnswers
        ? "your campaign answers its calls"
        : null,
  ].filter(Boolean);

  return (
    <>
      <div className="flex h-12 w-full shrink-0 items-center justify-center border-b border-border">
        <Tooltip
          content={status.detail ? `${status.text} · ${status.detail}` : status.text}
          side="left"
          open={tipOpen && !open}
          onOpenChange={setTipOpen}
        >
          <PopoverTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              // Radix opens the tooltip from a focus handler that stands down for a prevented event.
              onFocus={(e) => e.preventDefault()}
              aria-label={`Phone. ${facts.join(", ")}`}
              className={cn(
                "relative flex size-9 items-center justify-center rounded-full transition-colors",
                status.incoming
                  ? "bg-success-9 text-white hover:brightness-110"
                  : status.onCall
                    ? "bg-accent-3 text-accent-11 hover:bg-accent-4"
                    : open
                      ? "bg-surface-hover text-text"
                      : "text-text-muted hover:bg-surface-hover hover:text-text",
              )}
            >
              {status.incoming ? (
                <PhoneIncoming className="size-[18px]" aria-hidden />
              ) : (
                <Phone className="size-[18px]" aria-hidden />
              )}
              {status.incoming && <span className="animate-pulse-ring" aria-hidden />}
              {idle && (
                <span
                  className={cn(
                    "absolute right-1 top-1 size-2 rounded-full ring-2 ring-surface",
                    registrationDot(phone.registration),
                  )}
                  aria-hidden
                />
              )}
            </button>
          </PopoverTrigger>
        </Tooltip>
      </div>

      {/* The same facts as the button's name, for the eye — hidden from a screen reader, which
          has just heard them. */}
      {!idle && (
        <div className="flex w-full flex-col items-center gap-1.5 pt-2.5" aria-hidden>
          <span
            className={cn(
              // All of the rail's 56px, tightly set: "Incoming" is the longest word it shows.
              "max-w-full truncate text-2xs font-medium tracking-tight tabular",
              status.incoming ? "text-success-11" : status.held ? "text-warning-11" : "text-text",
            )}
          >
            {status.short}
          </span>
          {status.held && <Pause className="size-3.5 text-warning-11" />}
          {status.muted && <MicOff className="size-3.5 text-danger-11" />}
          {transcription.on && <Captions className="size-3.5 text-accent-11" />}
        </div>
      )}
      {/* Between calls, whether the ACD is sending this rep any: a word, in the state's tone — or
          that the dialer is offering them a lead. */}
      {idle && acd && (
        <div className="flex w-full flex-col items-center pt-2.5" aria-hidden>
          <span
            className={cn(
              "max-w-full truncate text-2xs font-medium tracking-tight",
              work.offer ? AVAILABILITY_TEXT.accent : AVAILABILITY_TEXT[acd.view.tone],
            )}
          >
            {work.offer ? "Offer" : acd.view.short}
          </span>
        </div>
      )}
      {(phone.autoAnswer || campaignAnswers) && (
        <div
          className={cn("flex flex-col items-center gap-1.5", idle && !acd ? "pt-2.5" : "pt-1.5")}
          aria-hidden
        >
          <Zap className="size-3.5 text-accent-11" />
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* Registration                                                                                */
/* ------------------------------------------------------------------------------------------ */

/**
 * Shown while the phone is not registered.
 *
 * The console connects on its own, so the common path through here is a second or two of
 * "Connecting…" that nobody has to act on — which is why the copy distinguishes that from the
 * two states where a rep really does have to do something. Telling somebody their phone is not
 * connected while it is busy connecting invites them to click a button that does nothing.
 */
export function ConnectView() {
  const phone = useSoftphone();
  const [busy, setBusy] = React.useState(false);
  const connecting = busy || phone.registration === "connecting";
  const extension = phone.credentials?.extension ?? "—";

  return (
    <div className="space-y-3 text-center">
      <p className="text-xs text-text-muted">
        {connecting ? (
          <>
            Connecting extension <span className="font-mono">{extension}</span> to the switch…
          </>
        ) : phone.registration === "failed" ? (
          "Your phone is not registered, so calls will not reach you."
        ) : (
          <>
            Your phone is off. Connecting registers extension{" "}
            <span className="font-mono">{extension}</span> in this tab.
          </>
        )}
      </p>
      <Button
        variant="default"
        className="h-10 w-full text-sm"
        loading={connecting}
        onClick={async () => {
          setBusy(true);
          try {
            await phone.connect();
          } finally {
            setBusy(false);
          }
        }}
      >
        <Phone />
        {phone.registration === "failed" ? "Try again" : "Connect"}
      </Button>
    </div>
  );
}

export function StandbyView() {
  const phone = useSoftphone();
  return (
    <div className="space-y-3 text-center">
      <p className="text-xs text-text-muted">
        Your phone is registered in another browser tab. Only one tab can hold the line, or
        inbound calls ring somewhere you are not looking.
      </p>
      <Button
        variant="default"
        className="h-10 w-full text-sm"
        onClick={() => void phone.takeOver()}
      >
        Use the phone here
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */
/* Calls                                                                                       */
/* ------------------------------------------------------------------------------------------ */

/**
 * The first call ringing. Two choices, both large, and nothing else on the screen.
 *
 * Answer takes focus on arrival so that Enter picks up — unless the rep is mid-keystroke in a
 * field somewhere, in which case the live-region announcement is the notification and the
 * keyboard stays where it was.
 */
function IncomingView({ call }: { call: Call }) {
  const phone = useSoftphone();
  const { title, subtitle, internalTransfer } = describeParty(call, phone.conference);
  // Decided at render, before the button mounts: `autoFocus` fires on mount and this is the
  // last moment the previous focus is still observable.
  const [takeFocus] = React.useState(() => !typingElsewhere());
  // Two callers at once, neither answered yet: the second is not `waiting` (there is no
  // conversation to protect) but it is still ringing, and a caller the dock does not show is a
  // caller who rings out.
  const others = phone.calls.filter(
    (c) => c.id !== call.id && c.direction === "inbound" && c.state === "ringing",
  );

  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      <RingingMark internalTransfer={internalTransfer} />
      <div className="w-full min-w-0">
        <p className="truncate text-lg font-semibold text-text">{title}</p>
        <p className="truncate text-xs text-text-muted">Incoming call · {subtitle}</p>
      </div>
      <AcdCallStrip callId={call.id} />
      <InviteStrip callId={call.id} />
      <LeadCardPanel callId={call.id} />
      <div className="grid w-full grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="h-11 text-sm text-danger-11"
          onClick={() => void phone.reject(call.id)}
        >
          <X />
          Decline
        </Button>
        <Button
          variant="default"
          className="h-11 bg-success-9 text-sm hover:brightness-110"
          onClick={() => void phone.answer(call.id)}
          autoFocus={takeFocus}
        >
          <Phone />
          Answer
        </Button>
      </div>
      {/* Blocking a caller who is ringing declines them as well. */}
      <BlockCallerAction call={call} decline />
      {others.length > 0 && (
        <div className="flex w-full flex-col gap-2 text-left">
          {others.map((c) => (
            <IncomingBanner key={c.id} call={c} decision={false} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The call the rep is on.
 *
 * Top to bottom, in order of urgency: a second call ringing (a decision with a deadline), who
 * this call is with, whoever is parked on hold, the five controls, End call.
 */
function CallView({
  call, onPush,
}: {
  call: Call;
  onPush: (view: SubView) => void;
}) {
  const phone = useSoftphone();
  const ringingIn = phone.calls.filter(
    (c) => c.id !== call.id && c.direction === "inbound" && c.state === "ringing",
  );
  const parked = phone.calls.filter((c) => c.id !== call.id && !ringingIn.includes(c));

  return (
    <div className="flex flex-col gap-3">
      {ringingIn.map((c) => (
        <IncomingBanner key={c.id} call={c} decision={c.id === phone.waiting?.id} />
      ))}

      <PartyHeader call={call} />
      <AcdCallStrip callId={call.id} />
      <ConsultBar />
      <DirectConsultBar callId={call.id} />
      <ParticipantBar callId={call.id} />
      <MonitorBar callId={call.id} />
      <LeadCardPanel callId={call.id} full />
      <BlockCallerAction call={call} />
      <CallRoster callId={call.id} />

      {parked.length > 0 && (
        <ul className="flex flex-col gap-1.5" aria-label="Other calls">
          {parked.map((c) => (
            <ParkedRow key={c.id} call={c} />
          ))}
        </ul>
      )}

      <CallControls call={call} onPush={onPush} />

      <EndCallButton call={call} />
    </div>
  );
}

function PartyHeader({ call }: { call: Call }) {
  const phone = useSoftphone();
  const acd = useAcdCall();
  const seconds = useCallDuration(call.answeredAt);
  // On a call in a room, how many are on it as the server counts them, as the Phone page says it.
  const people = useRoomPeople(call.id);
  const { title, subtitle, isRoom, internalTransfer } = describeParty(call, phone.conference, people);
  const transferring = phone.handingOver.includes(call.id);
  // The rep's record says who THEY held; the room says who is held in it, which is the only word
  // on a hold the ACD does itself — for a briefing, or for a handoff still waiting.
  const roomHeld = useCallerHeld(call.id);
  const callerHeld = (holdsInRoom(acd, call.id) && acd?.callerHeld === true) || roomHeld;
  const held = call.state === "held" || callerHeld;
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          held ? "bg-warning-2 text-warning-11" : "bg-accent-3 text-accent-11",
        )}
      >
        {isRoom ? (
          <Users className="size-[18px]" aria-hidden />
        ) : held ? (
          <Pause className="size-[18px]" aria-hidden />
        ) : internalTransfer ? (
          <Repeat className="size-[18px]" aria-hidden />
        ) : (
          <Phone className="size-[18px]" aria-hidden />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold text-text">{title}</p>
        <p className="truncate text-xs text-text-muted">{subtitle}</p>
      </div>
      <div className="shrink-0 text-right">
        {isEngaged(call.state) && (
          <p className="font-mono text-sm tabular text-text" aria-hidden>
            {formatDuration(seconds)}
          </p>
        )}
        <p className={cn("text-xs", held ? "text-warning-11" : "text-text-muted")}>
          {transferring && <Spinner className="mr-1 inline size-3 align-[-2px]" />}
          {stateText(call, transferring, callerHeld)}
        </p>
      </div>
    </div>
  );
}

